import { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { UpdateUncontrolledRiskInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import type { UpdateRiskInternalAuditResultInput } from './useUpdateControlledRiskInternalAuditResultTRPC';

const mapTrpcResponseToGraphQL =
  (): UpdateUncontrolledRiskInternalAuditResultMutation => ({
    update_risk_uncontrolled_internal_audit_result: {
      __typename: 'risk_uncontrolled_internal_audit_result_mutation_response',
      affected_rows: 1,
    },
  });

export const useUpdateUncontrolledRiskInternalAuditResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.internalAuditResult.updateRisk.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.internalAuditResultCount.queryKey(),
          }),
          // COI-471 parity: Apollo twin also evicts `internal_audit_report`.
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditReport.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateUncontrolledRiskInternalAuditResult: async (
      variables: UpdateRiskInternalAuditResultInput
    ): Promise<UpdateUncontrolledRiskInternalAuditResultMutation> => {
      await mutation.mutateAsync({
        ...variables,
        ControlType: RiskAssessmentResultControlType.Uncontrolled,
      });

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
